import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CaretRight, MapPin, Wine, Users, MagnifyingGlassPlus } from '@phosphor-icons/react';

const CountriesListPage = () => {
  const navigate = useNavigate();

  const countries = [
    {
      name: 'France',
      slug: 'france',
      description: 'Le berceau du vin, avec ses appellations prestigieuses et sa tradition viticole millénaire.',
      flag: '🇫🇷',
      totalArea: '789,000 ha',
      production: '46,000,000 hl',
      regions: 12,
      featuredRegions: ['Bordeaux', 'Provence', 'Bourgogne', 'Champagne'],
      featuredGrapes: ['Chardonnay', 'Cabernet Sauvignon', 'Merlot', 'Pinot Noir'],
      wineStyles: ['Rouges structurés', 'Blancs élégants', 'Effervescents', 'Vins doux']
    },
    {
      name: 'Espagne',
      slug: 'spain',
      description: 'Terre de vins rouges puissants et de cépages autochtones, avec une diversité climatique unique.',
      flag: '🇪🇸',
      totalArea: '961,000 ha',
      production: '35,000,000 hl',
      regions: 8,
      featuredRegions: ['Rioja', 'Ribera del Duero', 'Navarra', 'Priorat'],
      featuredGrapes: ['Tempranillo', 'Graciano', 'Garnacha', 'Verdejo'],
      wineStyles: ['Rouges tanniques', 'Blancs aromatiques', 'Vins fortifiés', 'Cava']
    },
    {
      name: 'Portugal',
      slug: 'portugal',
      description: 'Pays des vins fortifiés et des cépages uniques, avec une tradition viticole ancestrale.',
      flag: '🇵🇹',
      totalArea: '185,000 ha',
      production: '6,200,000 hl',
      regions: 8,
      featuredRegions: ['Douro', 'Alentejo', 'Dão', 'Vinho Verde'],
      featuredGrapes: ['Touriga Nacional', 'Alvarinho', 'Baga', 'Encruzado'],
      wineStyles: ['Porto', 'Vins rouges', 'Vins blancs', 'Vins effervescents']
    },
    {
      name: 'Italie',
      slug: 'italy',
      description: 'Diversité viticole millénaire et cépages autochtones, avec une tradition romaine ancestrale.',
      flag: '🇮🇹',
      totalArea: '702,000 ha',
      production: '48,000,000 hl',
      regions: 13,
      featuredRegions: ['Toscane', 'Piémont', 'Vénétie', 'Sicile'],
      featuredGrapes: ['Sangiovese', 'Nebbiolo', 'Pinot Grigio', 'Nero d\'Avola'],
      wineStyles: ['SuperTuscans', 'Barolo', 'Prosecco', 'Chianti']
    },
    {
      name: 'Argentine',
      slug: 'argentina',
      description: 'Terre du Malbec et des vins d\'altitude des Andes, avec un climat continental unique.',
      flag: '🇦🇷',
      totalArea: '215,000 ha',
      production: '12,000,000 hl',
      regions: 6,
      featuredRegions: ['Mendoza', 'Patagonie', 'Salta', 'San Juan'],
      featuredGrapes: ['Malbec', 'Torrontés', 'Cabernet Sauvignon', 'Chardonnay'],
      wineStyles: ['Rouges puissants', 'Blancs aromatiques', 'Vins d\'altitude']
    },
    {
      name: 'Chili',
      slug: 'chile',
      description: 'Vins de qualité entre océan et montagnes, avec une géographie exceptionnelle.',
      flag: '🇨🇱',
      totalArea: '82,000 ha',
      production: '10,000,000 hl',
      regions: 6,
      featuredRegions: ['Maipo', 'Colchagua', 'Curicó', 'Maule'],
      featuredGrapes: ['Carmenère', 'Cabernet Sauvignon', 'Sauvignon Blanc', 'Chardonnay'],
      wineStyles: ['Rouges structurés', 'Blancs frais', 'Vins côtiers']
    },
    {
      name: 'Australie',
      slug: 'australia',
      description: 'Innovation viticole et vins du Nouveau Monde, avec des climats diversifiés.',
      flag: '🇦🇺',
      totalArea: '92,000 ha',
      production: '13,000,000 hl',
      regions: 6,
      featuredRegions: ['Barossa Valley', 'Hunter Valley', 'Margaret River', 'McLaren Vale'],
      featuredGrapes: ['Shiraz', 'Chardonnay', 'Cabernet Sauvignon', 'Riesling'],
      wineStyles: ['Shiraz puissants', 'Chardonnay élégants', 'Vins innovants']
    },
    {
      name: 'Autriche',
      slug: 'austria',
      description: 'Vins blancs élégants et vins liquoreux prestigieux, avec une tradition millénaire.',
      flag: '🇦🇹',
      totalArea: '43,100 ha',
      production: '2,500,000 hl',
      regions: 4,
      featuredRegions: ['Niederösterreich', 'Burgenland', 'Steiermark', 'Wien'],
      featuredGrapes: ['Grüner Veltliner', 'Riesling', 'Zweigelt', 'Blaufränkisch'],
      wineStyles: ['Vins blancs secs', 'Vins liquoreux', 'Rouges élégants']
    },
    {
      name: 'Suisse',
      slug: 'switzerland',
      description: 'Vins de terroir entre lacs et montagnes, avec des cépages autochtones uniques.',
      flag: '🇨🇭',
      totalArea: '15,200 ha',
      production: '1,100,000 hl',
      regions: 5,
      featuredRegions: ['Valais', 'Vaud', 'Genève', 'Tessin'],
      featuredGrapes: ['Chasselas', 'Pinot Noir', 'Gamay', 'Merlot'],
      wineStyles: ['Vins de terroir', 'Chasselas', 'Pinot Noir']
    },
    {
      name: 'Mexique',
      slug: 'mexico',
      description: 'Berceau de la viticulture américaine, avec une histoire viticole ancestrale.',
      flag: '🇲🇽',
      totalArea: '5,000 ha',
      production: '300,000 hl',
      regions: 4,
      featuredRegions: ['Baja California', 'Coahuila', 'Querétaro'],
      featuredGrapes: ['Nebbiolo', 'Cabernet Sauvignon', 'Chardonnay', 'Chenin Blanc'],
      wineStyles: ['Vins de qualité', 'Vins d\'altitude', 'Vins traditionnels']
    },
    {
      name: 'Allemagne',
      slug: 'germany',
      description: 'Terre du Riesling et des vins liquoreux, avec une tradition viticole millénaire.',
      flag: '🇩🇪',
      totalArea: '104,000 ha',
      production: '9,000,000 hl',
      regions: 13,
      featuredRegions: ['Rheinhessen', 'Pfalz', 'Baden', 'Mosel'],
      featuredGrapes: ['Riesling', 'Spätburgunder', 'Müller-Thurgau', 'Silvaner'],
      wineStyles: ['Riesling', 'Vins liquoreux', 'Spätburgunder']
    }
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#4B164C', 
          marginBottom: '10px',
          fontWeight: '700'
        }}>
          Pays Viticoles du Monde
        </h1>
        <p style={{ 
          fontSize: '1.2rem', 
          color: '#666', 
          maxWidth: '700px', 
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Découvrez les principaux pays producteurs de vin et leurs caractéristiques uniques
        </p>
        <button
          onClick={() => navigate('/search')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#4B164C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background 0.2s',
            margin: '20px auto 0'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
          onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
        >
          <MagnifyingGlassPlus size={18} />
          Rechercher des vins
        </button>
      </div>

      {/* Countries Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
        gap: '30px' 
      }}>
        {countries.map((country, index) => (
          <div
            key={index}
            style={{
              background: '#fff',
              borderRadius: '16px',
              border: '2px solid #e8e0e8',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
          >
            {/* Country Header */}
            <div style={{ 
              padding: '25px', 
              background: 'linear-gradient(135deg, #4B164C 0%, #6d2c7a 100%)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <span style={{ fontSize: '2.5rem', marginRight: '15px' }}>
                  {country.flag}
                </span>
                <div>
                  <h2 style={{ 
                    fontSize: '1.6rem', 
                    margin: '0 0 5px 0',
                    fontWeight: '600'
                  }}>
                    {country.name}
                  </h2>
                  <p style={{ 
                    margin: '0', 
                    fontSize: '0.95rem',
                    opacity: '0.9',
                    lineHeight: '1.4'
                  }}>
                    {country.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Country Stats */}
            <div style={{ padding: '20px' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <MapPin size={24} color="#4B164C" style={{ marginBottom: '5px' }} />
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '3px' }}>
                    Surface
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#4B164C' }}>
                    {country.totalArea}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Wine size={24} color="#4B164C" style={{ marginBottom: '5px' }} />
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '3px' }}>
                    Production
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#4B164C' }}>
                    {country.production}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Users size={24} color="#4B164C" style={{ marginBottom: '5px' }} />
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '3px' }}>
                    Régions
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#4B164C' }}>
                    {country.regions}
                  </div>
                </div>
              </div>

              {/* Featured Regions */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Régions principales :
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px' 
                }}>
                  {country.featuredRegions.map((region, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '6px 12px',
                        background: '#f0e6f0',
                        borderRadius: '15px',
                        fontSize: '0.85rem',
                        color: '#4B164C',
                        fontWeight: '500'
                      }}
                    >
                      {region}
                    </span>
                  ))}
                </div>
              </div>

              {/* Featured Grapes */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Cépages emblématiques :
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px' 
                }}>
                  {country.featuredGrapes.map((grape, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '6px 12px',
                        background: '#e8e0e8',
                        borderRadius: '15px',
                        fontSize: '0.85rem',
                        color: '#666',
                        fontWeight: '500'
                      }}
                    >
                      {grape}
                    </span>
                  ))}
                </div>
              </div>

              {/* Wine Styles */}
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Styles de vin :
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px' 
                }}>
                  {country.wineStyles.map((style, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '6px 12px',
                        background: '#f8f6fa',
                        borderRadius: '15px',
                        fontSize: '0.85rem',
                        color: '#4B164C',
                        border: '1px solid #e8e0e8'
                      }}
                    >
                      {style}
                    </span>
                  ))}
                </div>
              </div>

              {/* Explore Button */}
              <Link
                to={`/info/countries/${country.slug}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '12px 20px',
                  background: '#4B164C',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Explorer {country.name}
                <CaretRight size={18} />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* Back to Overview */}
      <div style={{ 
        marginTop: '40px', 
        textAlign: 'center' 
      }}>
        <Link
          to="/info"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: '#f0e6f0',
            color: '#4B164C',
            textDecoration: 'none',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '500',
            border: '2px solid #4B164C',
            transition: 'all 0.2s'
          }}
          onMouseOver={e => {
            e.currentTarget.style.background = '#4B164C';
            e.currentTarget.style.color = 'white';
          }}
          onMouseOut={e => {
            e.currentTarget.style.background = '#f0e6f0';
            e.currentTarget.style.color = '#4B164C';
          }}
        >
          ← Retour au Guide du Vin
        </Link>
      </div>
    </div>
  );
};

export default CountriesListPage; 